/**
 * Filter-state och presentationslogik för rapport-vyn (S-08-04).
 *
 * Hålls fri från Svelte-runor så enheterna kan testas med vitest utan
 * att ladda komponentmiljön. ReportsView.svelte konsumerar dessa rena
 * funktioner via $derived.
 *
 * Datumintervall-filter görs klient-side i MVP1 — backend stöder bara
 * status + category enligt S-08-01. Listans hårdgräns är 200 rader så
 * detta fungerar i Hisings Backas pilot-skala.
 */

import type { AdminReport } from './reportsClient';
import {
  type ReportCategory,
  type ReportStatus,
  ALL_CATEGORIES,
  ALL_STATUSES,
  DEFAULT_STATUS_FILTER,
} from './reportLabels';

export type ReportFilterState = {
  /** Tom Set = alla kategorier visas. */
  categories: Set<ReportCategory>;
  /** Tom Set = alla statusar visas (men UI startar med default-mask). */
  statuses: Set<ReportStatus>;
  /** ISO-datumsträngar (yyyy-mm-dd). null = ingen gräns. */
  fromDate: string | null;
  toDate: string | null;
};

export function defaultFilterState(): ReportFilterState {
  return {
    categories: new Set<ReportCategory>(),
    statuses: new Set<ReportStatus>(DEFAULT_STATUS_FILTER),
    fromDate: defaultFromDate(),
    toDate: null,
  };
}

/**
 * Default datumintervall: senaste 30 dagar enligt UI-kontrakt vy 8.
 * Returnerar yyyy-mm-dd som matchar HTML date-input-format.
 */
export function defaultFromDate(now: Date = new Date()): string {
  const d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  return formatDateInput(d);
}

export function formatDateInput(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Filtrerar rapport-listan efter klient-side filter.
 *
 * Backend gör status/category-filter, men när vi tillåter multi-select
 * eller "alla" så hämtar vi bredare och filtrerar lokalt. Datumfilter
 * är ALLTID klient-side.
 */
export function applyFilter(
  reports: readonly AdminReport[],
  filter: ReportFilterState,
): AdminReport[] {
  const fromMs = filter.fromDate ? Date.parse(`${filter.fromDate}T00:00:00Z`) : null;
  // Inkluderande slut-datum: använd 24h efter angivet datum.
  const toMs = filter.toDate
    ? Date.parse(`${filter.toDate}T00:00:00Z`) + 24 * 60 * 60 * 1000
    : null;

  return reports.filter((report) => {
    if (filter.categories.size > 0 && !filter.categories.has(report.category)) {
      return false;
    }
    if (filter.statuses.size > 0 && !filter.statuses.has(report.status)) {
      return false;
    }
    const reportedMs = Date.parse(report.reportedAt);
    if (Number.isNaN(reportedMs)) {
      // Ogiltig timestamp filtreras bort hellre än att visas — hindrar
      // missvisande sortering/datum.
      return false;
    }
    if (fromMs !== null && reportedMs < fromMs) return false;
    if (toMs !== null && reportedMs >= toMs) return false;
    return true;
  });
}

/**
 * Toggle-helpers för Set-filter. Returnerar ny Set så Svelte $state
 * fångar förändringen.
 */
export function toggleCategory(
  current: Set<ReportCategory>,
  category: ReportCategory,
): Set<ReportCategory> {
  const next = new Set(current);
  if (next.has(category)) next.delete(category);
  else next.add(category);
  return next;
}

export function toggleStatus(current: Set<ReportStatus>, status: ReportStatus): Set<ReportStatus> {
  const next = new Set(current);
  if (next.has(status)) next.delete(status);
  else next.add(status);
  return next;
}

export { ALL_CATEGORIES, ALL_STATUSES };
